import { useEffect, useState } from 'react';
import PaymentsAPI from 'api/PaymentsAPI';
import { findStripeCustomerForTeam } from 'utils/stripeCustomer';

export type TeamTrialStatus = 'none' | 'trialing' | 'expired';

interface TeamTrialStatusResult {
  isLoading: boolean;
  status: TeamTrialStatus;
}

const LAPSED_STATUSES = ['canceled', 'unpaid', 'incomplete_expired', 'past_due'];

/**
 * 'expired' means a subscription ended during its trial -- i.e. the trial
 * lapsed without converting to a paid plan. Trials are created with
 * trial_settings.end_behavior.missing_payment_method = 'cancel' (see
 * payment.service.ts), so an unconverted trial ends as 'canceled' with
 * ended_at at (or before) trial_end. A converted subscription keeps its
 * trial_end too, but if it later lapses it does so after trial_end (or, for
 * past_due/unpaid, with no ended_at at all), so it isn't mislabeled here.
 * The status list is the 'canceled'/'unpaid'/'incomplete_expired'/'past_due'
 * grouping webhook.service.ts already uses for "not in good standing".
 */
function endedDuringTrial(sub: any): boolean {
  if (!LAPSED_STATUSES.includes(sub.status) || !sub.trial_end) return false;
  const endedAt = Number(sub.ended_at || sub.canceled_at);
  return Boolean(endedAt) && endedAt <= Number(sub.trial_end);
}

function resolveTrialStatus(matchedCustomer: any): TeamTrialStatus {
  // Two different shapes reach here: the cached `stripe_cus_info` wraps each
  // customer as `{name, data: <raw Stripe customer>}`, but the live
  // getCustomerByEmail fallback returns raw Stripe customer objects directly
  // (subscriptions on the customer itself, not nested under an extra `data`).
  const subscriptions =
    matchedCustomer?.data?.subscriptions?.data ||
    matchedCustomer?.subscriptions?.data ||
    [];

  // Checked explicitly first, across ALL subscriptions -- a team can have
  // more than one (e.g. an old cancelled one alongside the current trial),
  // and a single combined .find() would just grab whichever matching-status
  // one happens to come first in the array, not necessarily the trialing one.
  const trialingSub = subscriptions.find((sub: any) => sub.status === 'trialing');
  if (trialingSub) return 'trialing';

  // A paying subscription wins over any older lapsed trial on the same
  // customer -- that history isn't the team's current state.
  if (subscriptions.some((sub: any) => sub.status === 'active')) return 'none';

  return subscriptions.some(endedDuringTrial) ? 'expired' : 'none';
}

/**
 * Lifted out of Header.tsx's own isTrialing computation (which ProfileContent.tsx
 * separately duplicated) so both consumers -- and the license chip's trial/
 * expired suffix -- share one source instead of a third copy.
 *
 * `cur_user.stripe_cus_info` is fetched once at login and never refreshed
 * (see utils/stripeCustomer.ts's own docstring on this) -- a trial created
 * mid-session, e.g. during THIS SAME session's onboarding, is invisible in
 * the cache until the next login. A cache result of 'none' is therefore not
 * trustworthy on its own: this self-heals the same way
 * resolveStripeCustomerId does, by asking fresh when the cache has nothing.
 */
export function useTeamTrialStatus(cur_user: any): TeamTrialStatusResult {
  const [result, setResult] = useState<TeamTrialStatusResult>({
    isLoading: true,
    status: 'none',
  });

  useEffect(() => {
    let cancelled = false;
    // Drop the previous team's result straight away so the chip can't show
    // it while this team's live lookup is still in flight.
    setResult({ isLoading: true, status: 'none' });
    const teamName = cur_user?.selected_team?.name;

    const cachedCustomer = findStripeCustomerForTeam(cur_user?.stripe_cus_info, teamName);
    const cachedStatus = resolveTrialStatus(cachedCustomer);

    if (cachedStatus !== 'none') {
      setResult({ isLoading: false, status: cachedStatus });
      return undefined;
    }

    const email = cur_user?.basics?.email;
    if (!email) {
      setResult({ isLoading: false, status: 'none' });
      return undefined;
    }

    PaymentsAPI.getCustomerByEmail(email)
      .then((res: any) => {
        if (cancelled) return;
        const freshCustomer = findStripeCustomerForTeam(res?.result?.data, teamName);
        setResult({ isLoading: false, status: resolveTrialStatus(freshCustomer) });
      })
      .catch(() => {
        if (!cancelled) setResult({ isLoading: false, status: 'none' });
      });

    return () => {
      cancelled = true;
    };
  }, [cur_user?.selected_team?.name, cur_user?.stripe_cus_info, cur_user?.basics?.email]);

  return result;
}
